import { useQueryClient } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router'

import { errorDelCampo, MINIMO_DE_CONTRASENA } from '../api/cuenta'
import { ErrorDeLaApi, pedir } from '../api/nucleo'
import type { Usuario } from '../api/tipos'
import { PantallaDeAcceso } from '../componentes/Acceso'
import { Aviso, Boton, Campo } from '../componentes/ui'

/**
 * El final del registro desde wixy.app (núcleo: `src/cuenta/registro.ts`). El enlace del correo
 * trae, en el fragmento, un token firmado con el nombre, el correo, el nombre del negocio y el plan
 * elegido: aquí se enseñan para que la persona sepa qué está creando, y se pide la contraseña. Lo que
 * vale lo comprueba el núcleo; esto solo lee lo que ya viene a la vista.
 *
 * Al crearla se entra, y se sigue: con un plan de pago, a pagarlo en PayPal (`Creditos`, `?pagar=`);
 * con el Gratis, a crear la primera web.
 */
type Datos = { email: string; nombre: string; cuenta: string; plan: string }

export const leerRegistro = (hash: string, busqueda: string): { datos: Datos | null; token: null | string } => {
  const de = (texto: string) => new URLSearchParams(texto.replace(/^[#?]/, '')).get('token')
  const token = de(hash) ?? de(busqueda)
  if (!token || !/^[A-Za-z0-9_-]{1,1500}\.\d{1,15}\.[A-Za-z0-9_-]{20,100}$/.test(token)) return { datos: null, token: null }
  const [cuerpo, caduca] = token.split('.') as [string, string]
  if (Number(caduca) < Date.now()) return { datos: null, token }
  try {
    const bytes = Uint8Array.from(atob(cuerpo.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0))
    const d = JSON.parse(new TextDecoder().decode(bytes)) as Partial<Datos>
    if (typeof d.email !== 'string' || typeof d.cuenta !== 'string') return { datos: null, token }
    return { datos: { email: d.email, nombre: String(d.nombre ?? ''), cuenta: d.cuenta, plan: String(d.plan ?? 'gratis') }, token }
  } catch {
    return { datos: null, token }
  }
}

const NOMBRES_DE_PLAN: Record<string, string> = { gratis: 'Gratis', profesional: 'Profesional', tienda: 'Tienda', agencia: 'Agencia' }

export const Registro = () => {
  const navegar = useNavigate()
  const clienteDeConsultas = useQueryClient()
  const [{ datos, token }] = useState(() => leerRegistro(window.location.hash, window.location.search))
  const [clave, setClave] = useState('')
  const [repetida, setRepetida] = useState('')
  const [acepta, setAcepta] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [fallo, setFallo] = useState<unknown>(null)

  useEffect(() => {
    if (window.location.hash || window.location.search) window.history.replaceState(window.history.state, '', '/registro')
  }, [])

  const noCoinciden = repetida.length > 0 && clave !== repetida
  const dePago = datos ? datos.plan !== 'gratis' : false

  const enviar = async (e: FormEvent) => {
    e.preventDefault()
    if (!token || !datos || noCoinciden || !acepta) return
    setEnviando(true)
    setFallo(null)
    try {
      const r = await pedir<{ cliente: number; plan: string }>('/plataforma/registro/confirmar', { metodo: 'POST', cuerpo: { token, password: clave }, toleraAnonimo: true })
      const entrada = await pedir<{ user: Usuario }>('/usuarios/login?depth=1', { metodo: 'POST', cuerpo: { email: datos.email, password: clave }, toleraAnonimo: true })
      clienteDeConsultas.clear()
      clienteDeConsultas.setQueryData(['sesion'], { user: entrada.user })
      navegar(r.plan && r.plan !== 'gratis' ? `/e/${r.cliente}/creditos?pagar=${encodeURIComponent(r.plan)}` : `/e/${r.cliente}/sitios/nuevo`, { replace: true })
    } catch (error) {
      setFallo(error)
      setEnviando(false)
    }
  }

  if (!datos) {
    return (
      <PantallaDeAcceso>
        <h1 className="font-display text-[26px] font-semibold tracking-tight">{token ? 'Este enlace ha caducado' : 'Este enlace está incompleto'}</h1>
        <p className="mt-3 text-sm text-tenue">
          {token ? 'Los enlaces para crear una cuenta valen 24 horas.' : 'Ábrelo tal cual desde el correo, sin cortarlo.'} Vuelve a wixy.app, elige tu plan y te mandamos otro: tarda un momento.
        </p>
        <a className="mt-6 inline-block" href="https://wixy.app/es/precios">
          <Boton tipo="primario">Ir a los planes</Boton>
        </a>
      </PantallaDeAcceso>
    )
  }

  const yaCreada = fallo instanceof ErrorDeLaApi && fallo.datos.codigo === 'ya-creada'
  const errorDeLaClave = errorDelCampo(fallo, 'password')
  const general = fallo && !errorDeLaClave ? (fallo instanceof ErrorDeLaApi ? fallo.message : 'No hemos podido conectar. Comprueba tu conexión y vuelve a intentarlo.') : null

  return (
    <PantallaDeAcceso>
      <form onSubmit={enviar}>
        <span aria-hidden className="mb-5 flex size-11 items-center justify-center rounded-full bg-violeta-suave text-violeta">
          <Sparkles className="size-5" />
        </span>
        <h1 className="font-display text-[26px] font-semibold tracking-tight">Crea tu cuenta</h1>
        <p className="mt-2 text-sm text-tenue">
          {datos.nombre ? `${datos.nombre}, ` : ''}vas a crear la cuenta de <strong className="font-medium text-tinta">«{datos.cuenta}»</strong> con{' '}
          <strong className="font-medium text-tinta">{datos.email}</strong>, en el plan <strong className="font-medium text-tinta">{NOMBRES_DE_PLAN[datos.plan] ?? datos.plan}</strong>.
          {dePago ? ' Después de poner tu contraseña te llevamos a PayPal para pagarlo.' : ' Después creas tu web con el asistente.'}
        </p>
        <div className="mt-8 flex flex-col gap-4">
          <Campo
            autoComplete="new-password"
            autoFocus
            ayuda={`Al menos ${MINIMO_DE_CONTRASENA} caracteres. Una frase corta que recuerdes vale más que una palabra rara.`}
            error={errorDeLaClave}
            etiqueta="Contraseña"
            minLength={MINIMO_DE_CONTRASENA}
            onChange={(e) => setClave(e.target.value)}
            required
            type="password"
            value={clave}
          />
          <Campo
            autoComplete="new-password"
            error={noCoinciden ? 'No coincide con la de arriba.' : undefined}
            etiqueta="Repítela"
            onChange={(e) => setRepetida(e.target.value)}
            required
            type="password"
            value={repetida}
          />
          <label className="flex items-start gap-2 text-[13px] text-tinta-2">
            <input checked={acepta} className="mt-0.5 size-4 accent-violeta" onChange={(e) => setAcepta(e.target.checked)} required type="checkbox" />
            <span>
              Acepto los{' '}
              <a className="text-violeta underline underline-offset-2" href="https://wixy.app/es/legal/terminos" rel="noreferrer" target="_blank">
                términos
              </a>{' '}
              y la{' '}
              <a className="text-violeta underline underline-offset-2" href="https://wixy.app/es/legal/privacidad" rel="noreferrer" target="_blank">
                política de privacidad
              </a>
              .
            </span>
          </label>
          {general && (
            <Aviso tono={yaCreada ? 'info' : 'error'}>
              {general}
              {yaCreada && (
                <>
                  {' '}
                  <a className="font-medium text-violeta underline underline-offset-2" href="/entrar">
                    Entrar
                  </a>
                </>
              )}
            </Aviso>
          )}
          <Boton cargando={enviando} className="w-full" disabled={noCoinciden || !acepta} tipo="primario" type="submit">
            {dePago ? 'Crear mi cuenta e ir a pagar' : 'Crear mi cuenta'}
          </Boton>
        </div>
      </form>
    </PantallaDeAcceso>
  )
}
